import { useEffect, useState } from 'react';
import { Switch, Route, useHistory, useParams } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import {
  BookFill,
  CompassFill,
  Controller,
  TrophyFill,
} from 'react-bootstrap-icons';
import { getGuideCatalog, getGuide, expandGuideImageUrls } from '../services/GuideService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useLayout } from '../Context/LayoutProvider';

// ── Icon map for category icons stored in the DB ──────────────────────────────

const ICON_MAP = {
  'compass-fill': CompassFill,
  'controller':   Controller,
  'trophy-fill':  TrophyFill,
  'book-fill':    BookFill,
};

function CategoryIcon({ name, size = 11 }) {
  const Icon = ICON_MAP[name] ?? BookFill;
  return <Icon size={size} />;
}

// ── Splash screen ─────────────────────────────────────────────────────────────

function GuideSplash() {
  return (
    <div className="guide guide-splash white">
      <BookFill className="guide-splash-icon" />
      <h3>Paragon Conquer Guide</h3>
      <p>Welcome to the Paragon Conquer knowledge base.</p>
      <p>
        Select a guide from the sidebar to get started, or ask for help on our{' '}
        <a href="https://discord.gg/F7tUnrBMDY" target="_blank" rel="noreferrer">Discord</a>.
      </p>
    </div>
  );
}

// ── Guide viewer ──────────────────────────────────────────────────────────────

function GuideViewer() {
  const { categorySlug, guideSlug } = useParams();
  const history = useHistory();
  const [guide,   setGuide]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await getGuide(categorySlug, guideSlug);
        if (cancelled) return;
        if (data.redirect) {
          history.replace(data.redirect);
          return;
        }
        setGuide(data);
      } catch (err) {
        if (!cancelled)
          setError(err.message === 'not-found' ? 'Guide not found.' : 'Failed to load guide.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [categorySlug, guideSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="guide"><LoadingSpinner /></div>;
  if (error)   return <div className="guide"><p style={{ color: 'var(--clr-error)' }}>{error}</p></div>;

  return (
    <article className="guide">
      <h3>{guide.Title}</h3>
      <div dangerouslySetInnerHTML={{ __html: expandGuideImageUrls(guide.Content) }} />
    </article>
  );
}

// ── Root guide layout ─────────────────────────────────────────────────────────

function Guide() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [navError, setNavError] = useState(false);
  const { setFullWidth } = useLayout();

  useEffect(() => {
    setFullWidth(true);
    return () => setFullWidth(false);
  }, [setFullWidth]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getGuideCatalog();
        if (!cancelled) setCatalog(data);
      } catch {
        if (!cancelled) setNavError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="guide-layout">

      {/* Sidebar nav */}
      <aside className="guide-sidebar">
        <div className="gsb-header">
          <BookFill size={14} /> Guide
        </div>
        {loading ? (
          <div style={{ padding: '1rem' }}><LoadingSpinner /></div>
        ) : navError ? (
          <p style={{ padding: '0.75rem', color: 'var(--clr-error)', fontSize: '0.82rem' }}>
            Failed to load navigation.
          </p>
        ) : (
          <nav>
            {catalog.map(cat => (
              <div key={cat.Id} className="gsb-section">
                <div className="gsb-section-title">
                  <CategoryIcon name={cat.Icon} /> {cat.Name}
                </div>
                {cat.Guides.map(g => (
                  <LinkContainer key={g.Id} to={`/guide/${cat.Slug}/${g.Slug}`} exact>
                    <a className="gsb-link">{g.Title}</a>
                  </LinkContainer>
                ))}
              </div>
            ))}
          </nav>
        )}
      </aside>

      {/* Content area */}
      <div className="guide-content">
        <Switch>
          <Route path="/guide/:categorySlug/:guideSlug" component={GuideViewer} />
          <Route path="/guide" component={GuideSplash} />
        </Switch>
      </div>

    </div>
  );
}

export { Guide };
